/**
 * Attachments panel: lists documents linked to a record and uploads new ones.
 * Used on every record where supporting evidence is useful.
 */
import type { AttachableType } from "@shared/constants.ts";
import { DOCUMENT_CATEGORIES } from "@shared/constants.ts";
import { useRef, useState } from "react";
import { Button, Form, ListGroup, Spinner } from "react-bootstrap";
import { Link } from "react-router";
import { api } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";
import { formatBytes, useFormat } from "../lib/format.ts";
import { useApi, useInvalidateAll } from "../lib/query.ts";
import { Icon } from "./ui/Icon.tsx";
import { ErrorAlert, Section } from "./ui/kit.tsx";

export interface DocumentRow {
  id: string;
  name: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  category: string;
  createdAt: string;
  uploadedByName: string | null;
  addressLine1: string | null;
  propertyId: string | null;
  notes: string;
  links: Array<{ entityType: string; entityId: string }>;
}

export function UploadControl({
  entityType,
  entityId,
  defaultCategory = "other",
  onUploaded,
  compact,
}: {
  entityType?: AttachableType;
  entityId?: string;
  defaultCategory?: string;
  onUploaded?: (doc: DocumentRow) => void;
  compact?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState(defaultCategory);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const invalidate = useInvalidateAll();

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("file", file);
        form.append("category", category);
        if (entityType && entityId) {
          form.append("entityType", entityType);
          form.append("entityId", entityId);
        }
        const doc = await api.upload<DocumentRow>("/api/documents", form);
        onUploaded?.(doc);
      }
      await invalidate();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div>
      <ErrorAlert error={error} />
      {/* Drag-and-drop is an enhancement; the Upload button is the accessible control. */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: drop zone enhancement */}
      <div
        className={`d-flex flex-wrap gap-2 align-items-center ${compact ? "" : "upload-drop p-3"}`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          void upload(e.dataTransfer.files);
        }}
      >
        <Form.Select
          size="sm"
          aria-label="Document category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          style={{ width: "auto" }}
        >
          {DOCUMENT_CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </Form.Select>
        <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => input.current?.click()}>
          {busy ? <Spinner size="sm" animation="border" /> : <Icon name="upload" size={14} />} Upload file
        </Button>
        {!compact && (
          <span className="small text-muted">or drag files here · PDF, images, Office documents</span>
        )}
        <input
          ref={input}
          type="file"
          multiple
          hidden
          data-testid="file-input"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.txt,.csv,.doc,.docx,.xls,.xlsx,.odt,.ods"
          onChange={(e) => void upload(e.target.files)}
        />
      </div>
    </div>
  );
}

export function DocumentList({ docs }: { docs: DocumentRow[] }) {
  const f = useFormat();
  if (docs.length === 0) return <p className="text-muted small mb-0">No documents attached yet.</p>;
  return (
    <ListGroup variant="flush">
      {docs.map((d) => (
        <ListGroup.Item key={d.id} className="d-flex justify-content-between align-items-center gap-2 px-0">
          <div className="min-w-0">
            <Link to={`/documents/${d.id}`} className="d-flex align-items-center gap-2 text-truncate">
              <Icon name="paperclip" size={14} />
              <span className="text-truncate">{d.name}</span>
            </Link>
            <div className="small text-muted">
              {formatBytes(d.sizeBytes)} · {f.date(d.createdAt)}
            </div>
          </div>
          <a
            className="btn btn-sm btn-light"
            href={`/api/documents/${d.id}/content`}
            aria-label={`Download ${d.name}`}
          >
            <Icon name="download" size={14} />
          </a>
        </ListGroup.Item>
      ))}
    </ListGroup>
  );
}

export function Attachments({
  entityType,
  entityId,
  title = "Documents",
}: {
  entityType: AttachableType;
  entityId: string;
  title?: string;
}) {
  const { can } = useAuth();
  const q = useApi<DocumentRow[]>(can("documents:read") ? "/api/documents" : null, { entityType, entityId });
  if (!can("documents:read")) return null;
  return (
    <Section title={title}>
      {can("documents:write") && (
        <div className="mb-3">
          <UploadControl entityType={entityType} entityId={entityId} compact />
        </div>
      )}
      <DocumentList docs={q.data ?? []} />
    </Section>
  );
}
